'use client';

import React, { useEffect } from 'react';
import { Box, Text, InlineStack, Spinner, Card } from '@shopify/polaris';
import { MediaAsset, PlayAppMediaAssetStatus } from '@/types/media';
import { NoContentButton } from '@/features/media-modal/components/modal-sections/sections-content/NoContentButton';
import useProviderConnectionStatus from '@/hooks/useProviderConnectionStatus';
import { IntegrationService } from '@/features/integrations';
import { useUserStore } from '@/providers/userStoreProvider';
import {
  MediaAssetProviders,
  MediaAssetProvidersDisplayNames,
} from '@/features/ai-video-creator/stores/productMediaSlice';
import { ProviderConnectorAssetsPreview } from './ProviderConnectorAssetsPreview';

type ProviderConnectorProps = {
  provider: MediaAssetProviders;
  assets: MediaAsset[];
  isLoading: boolean;
  invalidateQuery: () => void;
};

const ProviderConnector = ({ provider, assets, isLoading, invalidateQuery }: ProviderConnectorProps) => {
  const account = useUserStore((state) => state.account);
  const { owner: userId, appKey } = account;

  const { appsByProvider } = useProviderConnectionStatus();

  const providerDisplayName = MediaAssetProvidersDisplayNames[provider];

  const integrationAction = IntegrationService.actionByProvider[provider];
  const app = appsByProvider[provider]?.[0];
  const isConnected = !!app;
  const providerData = JSON.parse(app?.data || '{}');
  const { lastSuccessfulImport } = providerData;
  const hasAutoImport = app?.importSettings === 'auto';

  const areAssetsBeingImported =
    !assets.length || assets?.some((asset) => asset.status !== PlayAppMediaAssetStatus.Done);

  const isImporting = hasAutoImport && !isLoading && (!lastSuccessfulImport || areAssetsBeingImported);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (!interval && isImporting && lastSuccessfulImport) {
      interval = setInterval(() => {
        invalidateQuery();
      }, 4000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isImporting, invalidateQuery]);

  return (
    <div>
      {!isConnected && !isLoading && integrationAction && (
        <NoContentButton
          provider={provider}
          actionText="Connect to import your media"
          actionLabel={`Connect ${providerDisplayName}`}
          emptyStateHeading="Integration not connected"
          onAction={() => integrationAction({ userId, appKey })}
        />
      )}

      {isImporting && (
        <Card>
          <Box padding="400">
            <InlineStack align="center">
              <Text as="p" variant="bodyMd">
                We are importing your {providerDisplayName} media from the last 90 days. This can take up to a minute...
              </Text>
            </InlineStack>
            <InlineStack align="center">
              <Spinner accessibilityLabel="Importing videos" size="small" />
            </InlineStack>
          </Box>
          <ProviderConnectorAssetsPreview provider={provider} />
        </Card>
      )}
    </div>
  );
};

export default ProviderConnector;
